'use client';

import { createContext, useContext } from 'react';

import {
  type DeprecatedPlacementValue,
  type PlacementValue,
} from '../../../internal/types/placement';

interface PopoverContextProps {
  /**
   * Fallback for `Popover.Popup`'s `placement` prop. Set when consumers pass the deprecated root-level `placement`.
   */
  placement?: PlacementValue | DeprecatedPlacementValue;
  /**
   * Fallback for `Popover.Popup`'s `offset` prop.
   */
  offset?: number;
  /**
   * Fallback for `Popover.Popup`'s `disableFlip` prop. Set when consumers pass the deprecated root-level `flip={false}`.
   */
  disableFlip?: boolean;
  /**
   * Fallback for `Popover.Popup`'s `disableShift` prop. Set when consumers pass the deprecated root-level `shift={false}`.
   */
  disableShift?: boolean;
  /**
   * Fallback for `Popover.Popup`'s `mountStrategy` prop. Set when consumers pass the deprecated root-level `keepMounted`.
   */
  mountStrategy?: 'lazy' | 'eager';
  /**
   * Fallback for `Popover.Popup`'s `width` prop. Set when consumers pass the deprecated root-level `matchTriggerWidth`.
   */
  width?: 'auto' | 'trigger' | 'min-trigger';
  /**
   * Fallback for `Popover.Popup`'s `disablePortal` prop. Set when consumers pass the deprecated root-level `disablePortal`.
   */
  disablePortal?: boolean;
  /**
   * Fallback for `Popover.Trigger`'s `enableHover` prop. Set when consumers pass the deprecated root-level `isHoverable`.
   */
  enableHover?: boolean;
  /**
   * Current open state of the popover. Sub-components that cannot read it from Base UI's
   * own state (e.g. `Popover.Close`, whose Base UI state object is empty) consume this.
   */
  isOpen: boolean;
}

const PopoverContext = createContext<PopoverContextProps | null>(null);

PopoverContext.displayName = 'PopoverContext';

function usePopoverContext() {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error(
      'Attempted to render a popover component outside of a <Popover> component tree.'
    );
  }

  return context;
}

export { PopoverContext, type PopoverContextProps, usePopoverContext };
